import { IAlbum } from '@/data/models/album';
import { ITrack } from '@/data/models/track';
import useUpdateProductMutation from '@/hooks/useUpdateProductMutation';
import { cloneDeep } from 'lodash';

/**
 * Custom hook that provides functionality to move selected tracks to a specified volume (disc or side).
 * It handles the local state mutation, track re-sequencing, and triggers the backend API update.
 *
 * @returns An object containing the `moveTracks` function and the mutation's `isPending` state.
 */
export default function useMoveTracksToVolume() {
  const updateProductMutation = useUpdateProductMutation();

  const moveTracks = (
    albumId: string,
    album: IAlbum,
    allTracks: ITrack[],
    selectedTracksToMove: ITrack[],
    targetVolume: number,
    onSuccess?: () => void,
    onError?: () => void
  ) => {
    const updatedAlbum = cloneDeep(album);
    const updatedTracks = cloneDeep(allTracks);

    const tracksToMove = updatedTracks.filter((t) =>
      selectedTracksToMove.some((st) => st.id === t.id)
    );

    tracksToMove.sort((a, b) =>
      a.discOrSideNumber !== b.discOrSideNumber
        ? a.discOrSideNumber - b.discOrSideNumber
        : a.trackNumber - b.trackNumber
    );

    for (const track of tracksToMove) {
      track.discOrSideNumber = targetVolume;
      track.trackNumber = 9999 + track.trackNumber;
    }

    const volumes = Array.from(
      new Set(updatedTracks.map((t) => t.discOrSideNumber))
    );

    for (const vol of volumes) {
      const volTracks = updatedTracks
        .filter((t) => t.discOrSideNumber === vol)
        .sort((a, b) => a.trackNumber - b.trackNumber);

      let j = 0;

      for (const track of volTracks) {
        track.trackNumber = ++j;
      }
    }

    updatedAlbum.tracks = updatedTracks;

    updateProductMutation.mutate(
      {
        albumId,
        album: updatedAlbum,
        state: album.albumState,
      },
      { onSuccess, onError }
    );
  };

  return {
    moveTracks,
    isPending: updateProductMutation.isPending,
  };
}
